/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Modal } from '@blend/shared-react';
import { mixins, colors } from '@blend/consumer-components';
import { notaryAPI } from '../../../../../api/notary';
import { CommonAPI } from '../../../../../api/common/responses';
import { getStyleConfig, styles as modalStyles } from '../../../../../components/common/Modals/styles';
import { NotaryAPI } from '../../../../../api/notary/types';
import { useRootSelector } from '../../../../../hooks/useRootSelector';
import { zIndex as zIndexes } from '../../../../../style/zIndex';
import { ActionButton } from '@components/ActionButton/ActionButton';
import { CheckboxWithDisplay } from '@components/form/Inputs/CheckboxWithDisplay/CheckboxWithDisplay';

interface VerifyUserModal {
  modalOpen: boolean;
  setModalOpen(modalOpen: boolean): void;
  user: CommonAPI.User;
}

const styles = StyleSheet.create({
  ...modalStyles,
  content: {
    ...modalStyles.content,
    padding: 40,
  },
  heading: {
    ...modalStyles.heading,
    textAlign: 'center',
    marginBottom: 10,
  },
  wrapper: {
    marginTop: '-10px',
  },
  checkBox: {
    marginTop: 15,
    marginBottom: 25,
    display: 'flex',
    font: 'gotham',
  },
  verifyIdInfoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  idWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  idBackground: {
    border: '1px solid #BABECA',
    backgroundColor: '#F8F8FA',
    width: '85%',
    height: 276,
    borderRadius: 5,
    marginTop: 20,
    marginBottom: 20,
    position: 'relative',
    top: 0,
    left: 0,
  },
  id: {
    position: 'relative',
    top: '3%',
    left: '2%',
    width: '96%',
    height: 260,
  },
});

const overlayStyleConfig = {
  backgroundColor: mixins.rgba(0, 0, 0, 0.2),
  top: '230px',
  zIndex: zIndexes.modal,
};

export const VerifyUserModal: React.FC<VerifyUserModal> = ({ modalOpen, setModalOpen, user }) => {
  const { firstName, lastName, participantSessionId } = user;
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [reviewData, setReviewData] = useState<NotaryAPI.ParticipantSessionForReviewResponse>();

  const isMobileView = useRootSelector(state => state.layout.isMobileView);

  useEffect(() => {
    async function fetchReviewData() {
      if (participantSessionId) {
        const reviewDataResult = await notaryAPI.calls.getParticipantSessionDetailsForReview(participantSessionId);
        setReviewData(reviewDataResult);
      }
    }

    fetchReviewData();
  }, [participantSessionId]);

  const checkboxText =
    "I confirm that the photo ID above matches the corresponding participant's appearance in the live video feed.";

  const handleVerification = async () => {
    if (participantSessionId && isChecked) {
      await notaryAPI.calls.approveParticipantSession(participantSessionId);
    }
    setModalOpen(false);
  };

  return (
    <div className={css(styles.wrapper)}>
      {reviewData && (
        <Modal
          overlayStyleConfig={overlayStyleConfig}
          styleConfig={{
            ...getStyleConfig(isMobileView),
            backgroundColor: colors.white,
            boxShadow: mixins.boxShadow(0, 2, 4, 0, mixins.fade(colors.black, 0.15)),
          }}
          isOpen={modalOpen}
          handleClose={() => setModalOpen(false)}
          contentLabel={'Verify Photo ID'}
          data-it={`VerifyUserModal-${user.participantSessionId}`}
        >
          <div className={css(styles.content)}>
            <h1 className={css(styles.heading)}>Verify ID</h1>
            <div>
              <div data-it="verify-id-name" className={css(styles.verifyIdInfoItem)}>
                <div>Name:</div>
                <div>
                  <b>
                    {firstName} {lastName}
                  </b>
                </div>
              </div>
              <div data-it="verify-id-dob" className={css(styles.verifyIdInfoItem)}>
                <div>Date of Birth:</div>
                <div>
                  <b>{reviewData.dateOfBirth}</b>
                </div>
              </div>
            </div>
            <div className={css(styles.idWrapper)}>
              <div className={css(styles.idBackground)}>
                <img
                  className={css(styles.id)}
                  src={`${reviewData.identityDocumentImgTags[0]}`}
                  alt="document image front side"
                />
              </div>
            </div>
            <div className={css(styles.checkBox)}>
              <CheckboxWithDisplay
                id={'verifyIdCheckbox'}
                displayParts={[checkboxText]}
                onClick={() => {
                  setIsChecked(!isChecked);
                }}
                checked={isChecked}
                onChange={() => {}}
              />
            </div>
            <div className={css(styles.primaryButton)}>
              <ActionButton data-dd-privacy="allow" onClick={() => handleVerification()}>
                Done
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
